import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  TELEPHONY_PROVIDER_SPEC,
  CallLanguageOption,
  TTS_PROVIDER_SPEC,
  TelephonyProvider,
  TelephonyProviderDescriptor,
  TenantVoiceConfig,
  TtsProvider,
  voiceConfigService,
} from '../../../../services/voiceConfigService';
import { fileService } from '../../../../services/fileService';
import { agentInstanceService } from '../../../../services/agentService';
import { Button } from '../../../../components/ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../../../../components/ui/Field';
import { Surface } from '../../../../components/ui/Surface';

interface VoiceCredentialsProps {
  tenantId: string;
}

/** Used only until (or if) the server's list (GET /core/voice/call-languages) loads. */
const FALLBACK_LANGUAGE_OPTIONS: CallLanguageOption[] = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-IN', label: 'English (India)' },
  { code: 'en-GB', label: 'English (UK)' },
];

// Twilio built-in voices — value is passed directly to Twilio <Say voice="...">
const TWILIO_VOICE_OPTIONS = [
  { value: 'Polly.Joanna-Generative', label: 'Joanna (US Female, Generative)' },
  { value: 'Polly.Matthew-Generative', label: 'Matthew (US Male, Generative)' },
  { value: 'Polly.Kajal-Generative', label: 'Kajal (Indian English, Generative)' },
  { value: 'Polly.Lupe-Generative', label: 'Lupe (Spanish/LatAm, Generative)' },
  { value: 'Polly.Pedro-Generative', label: 'Pedro (Spanish/LatAm, Generative)' },
  { value: 'Polly.Ayanda-Generative', label: 'Ayanda (South African, Generative)' },
  { value: 'Polly.Ruth-Neural', label: 'Ruth (US Female, Neural)' },
  { value: 'Polly.Amy-Neural', label: 'Amy (UK Female, Neural)' },
  { value: 'Polly.Stephen-Neural', label: 'Stephen (US Male, Neural)' },
  { value: 'Polly.Matthew-Neural', label: 'Matthew (US Male, Neural)' },
  { value: 'alice', label: 'Alice (Default)' },
];

// Supertonic (self-hosted) preset voices. F* female, M* male.
const SUPERTONIC_VOICE_OPTIONS = [
  { value: 'F1', label: 'F1 (Female)' },
  { value: 'F2', label: 'F2 (Female)' },
  { value: 'F3', label: 'F3 (Female)' },
  { value: 'F4', label: 'F4 (Female)' },
  { value: 'F5', label: 'F5 (Female)' },
  { value: 'M1', label: 'M1 (Male)' },
  { value: 'M2', label: 'M2 (Male)' },
  { value: 'M3', label: 'M3 (Male)' },
  { value: 'M4', label: 'M4 (Male)' },
  { value: 'M5', label: 'M5 (Male)' },
  { value: 'andromeda', label: 'Andromeda (Custom)' },
  { value: 'arcas', label: 'Arcas (Custom)' },
  { value: 'asteria', label: 'Asteria (Custom)' },
  { value: 'electra', label: 'Electra (Custom)' },
  { value: 'sarah_eve', label: 'Sarah Eve (Custom)' },
  { value: 'veda_sky', label: 'Veda Sky (Custom)' },
  { value: 'amanda', label: 'Amanda (Custom)' },
  { value: 'charlotte', label: 'Charlotte (Custom)' },
  { value: 'bella', label: 'Bella (Custom)' },
];

// Sarvam bulbul:v4-flash speakers suited to customer calls (enhi = Hinglish). The full list:
// https://docs.sarvam.ai/api-reference-docs/text-to-speech/convert — any of them can be typed in.
const SARVAM_VOICE_OPTIONS = [
  { value: 'simran_enhi_customer', label: 'Simran — Hinglish, customer care (female)' },
  { value: 'ishita_enhi_customer', label: 'Ishita — Hinglish, customer care (female)' },
  { value: 'shalini_enhi_customer', label: 'Shalini — Hinglish, customer care (female)' },
  { value: 'ritu_hi_medical', label: 'Ritu — Hindi, medical (female)' },
  { value: 'aarti_hi_customer', label: 'Aarti — Hindi, customer care (female)' },
  { value: 'sunny_enhi_customer', label: 'Sunny — Hinglish, customer care (male)' },
  { value: 'shubh_hi_customer', label: 'Shubh — Hindi, customer care (male)' },
  { value: 'ratan_hi_customer', label: 'Ratan — Hindi, customer care (male)' },
];

const DEFAULT_VOICE: Record<TtsProvider, string> = {
  twilio: 'Polly.Joanna-Generative',
  elevenlabs: '21m00Tcm4TlvDq8ikWAM', // Rachel — fallback until voices load
  supertonic: 'F1',
  fish: '', // empty = Fish default voice; paste a reference_id to pick one
  sarvam: 'simran_enhi_customer',
};

const VOICE_PREVIEW_TEXT = 'Hello! This is a quick voice preview.';
const CUSTOM_VOICE_OPTION = '__custom__';
const FISH_DEFAULT_OPTION = '__fish_default__';

const EMPTY: TenantVoiceConfig = {
  telephonyProvider: 'twilio',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioPhoneNumber: '',
  telnyxApiKey: '',
  telnyxPhoneNumber: '',
  ttsProvider: 'elevenlabs',
  ttsApiKey: '',
  ttsBaseUrl: '',
  ttsVoiceId: '',
  ttsModelId: '',
  sttProvider: 'deepgram',
  sttApiKey: '',
  sttModel: '',
  language: 'en-US',
  keyterms: '',
  idleTimeoutSeconds: 10,
  idleMaxPrompts: 2,
  enableHoldMusic: true,
  enableAmbientMusic: true,
  ambientMusicKey: '',
  ambientMusicBucket: '',
  hasTwilioAuthToken: false,
  hasTelnyxApiKey: false,
  hasTtsApiKey: false,
  hasSttApiKey: false,
};

/**
 * Telephony and speech credentials for a whitelabel tenant.
 *
 * Only the fields the chosen provider actually uses are shown: Twilio's built-in
 * voices need no key, Supertonic is self-hosted so it needs a URL instead, and
 * Fish Audio needs both. Showing every field for every provider invites someone
 * to fill in one that is never read.
 *
 * Secrets load pre-filled with their saved (decrypted) value — see
 * voiceConfigService.ts — and are edited in place like any other field;
 * clearing one to blank and saving removes the stored credential.
 */
const VoiceCredentials: React.FC<VoiceCredentialsProps> = ({ tenantId }) => {
  const [config, setConfig] = useState<TenantVoiceConfig>(EMPTY);
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  // Carriers and the fields each needs, from the server's provider registry.
  const [telephonyProviders, setTelephonyProviders] = useState<TelephonyProviderDescriptor[]>([]);
  // Edits to declared-field providers (Plivo, …), keyed by provider code. Secrets start blank:
  // they are write-only, so blank means "keep the saved one".
  const [providerFields, setProviderFields] = useState<Record<string, Record<string, string>>>({});

  const [languageOptions, setLanguageOptions] = useState<CallLanguageOption[]>(FALLBACK_LANGUAGE_OPTIONS);

  // The server decides which call languages exist, so adding one needs no frontend change.
  useEffect(() => {
    voiceConfigService
      .listCallLanguages()
      .then((res: any) => {
        if (Array.isArray(res?.languages) && res.languages.length) setLanguageOptions(res.languages);
      })
      .catch(() => { /* keep the fallback list */ });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await voiceConfigService.get(tenantId);
      setConfig({ ...EMPTY, ...(res?.config ?? {}) });
      setTelephonyProviders(Array.isArray(res?.telephonyProviders) ? res.telephonyProviders : []);
      const stored = (res?.config?.telephonyCredentials ?? {}) as Record<string, { values: Record<string, string> }>;
      setProviderFields(Object.fromEntries(Object.entries(stored).map(([code, v]) => [code, { ...(v?.values ?? {}) }])));
    } catch {
      setStatus({ tone: 'error', text: 'Could not load voice credentials.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Voice picker: live-fetched ElevenLabs/Fish voices, or a Supertonic/Twilio
  // preset list, plus a preview button. Mirrors the agent-level Voice Config
  // tab's picker — same list/preview endpoints (platform-key-based; see note
  // where it's rendered below).
  const [elevenVoices, setElevenVoices] = useState<Array<{ voice_id: string; name: string }>>([]);
  const [fishVoices, setFishVoices] = useState<Array<{ id: string; name: string; languages: string[] }>>([]);
  const [customVoiceMode, setCustomVoiceMode] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voicesWarning, setVoicesWarning] = useState('');
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (config.ttsProvider !== 'elevenlabs') return;
    setLoadingVoices(true);
    setVoicesWarning('');
    agentInstanceService.getElevenLabsVoices()
      .then((data) => {
        setElevenVoices(data.voices || []);
        setVoicesWarning(data.warning || '');
        setLoadingVoices(false);
      })
      .catch(() => {
        setElevenVoices([
          { voice_id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel' },
          { voice_id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella' },
          { voice_id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh' },
          { voice_id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam' },
        ]);
        setVoicesWarning('Could not load ElevenLabs voices. Showing fallback voices only.');
        setLoadingVoices(false);
      });
  }, [config.ttsProvider]);

  useEffect(() => {
    if (config.ttsProvider !== 'elevenlabs' || !elevenVoices.length) return;
    const v = config.ttsVoiceId;
    if (v && !elevenVoices.some((ev) => ev.voice_id === v)) setCustomVoiceMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevenVoices]);

  useEffect(() => {
    if (config.ttsProvider !== 'fish') return;
    setLoadingVoices(true);
    setVoicesWarning('');
    agentInstanceService.getFishVoices()
      .then((data) => {
        setFishVoices(data.voices || []);
        setVoicesWarning(data.warning || '');
        setLoadingVoices(false);
      })
      .catch(() => {
        setFishVoices([]);
        setVoicesWarning('Could not load Fish Audio voices. Enter a reference ID manually.');
        setLoadingVoices(false);
      });
  }, [config.ttsProvider]);

  useEffect(() => {
    if (config.ttsProvider !== 'fish' || !fishVoices.length) return;
    const v = config.ttsVoiceId;
    if (v && !fishVoices.some((fv) => fv.id === v)) setCustomVoiceMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fishVoices]);

  const handleProviderChange = (provider: TtsProvider) => {
    setCustomVoiceMode(false);
    // One provider's key, model and URL are never valid for another — and a field the new provider
    // doesn't show would still be saved — so switching clears them ("not set" falls back to the
    // platform's defaults, or for a Sarvam key, the Speech-to-text Sarvam key).
    setConfig((prev) =>
      provider === prev.ttsProvider
        ? prev
        : { ...prev, ttsProvider: provider, ttsVoiceId: DEFAULT_VOICE[provider], ttsApiKey: '', ttsModelId: '', ttsBaseUrl: '' },
    );
  };

  const handleElevenVoiceSelect = (value: string) => {
    if (value === CUSTOM_VOICE_OPTION) {
      setCustomVoiceMode(true);
      return;
    }
    setCustomVoiceMode(false);
    setConfig((prev) => ({ ...prev, ttsVoiceId: value }));
  };

  const handleFishVoiceSelect = (value: string) => {
    if (value === CUSTOM_VOICE_OPTION) {
      setCustomVoiceMode(true);
      return;
    }
    setCustomVoiceMode(false);
    setConfig((prev) => ({ ...prev, ttsVoiceId: value === FISH_DEFAULT_OPTION ? '' : value }));
  };

  const isElevenLabs = config.ttsProvider === 'elevenlabs';
  const isSupertonic = config.ttsProvider === 'supertonic';
  const isFish = config.ttsProvider === 'fish';
  const isSarvam = config.ttsProvider === 'sarvam';

  const handlePreviewVoice = async () => {
    if (!isElevenLabs && !isSupertonic && !isFish && !isSarvam) return;
    if (!config.ttsVoiceId && !isFish) {
      toast.error('Select a voice first.');
      return;
    }

    setPreviewingVoice(true);
    try {
      const blob = await agentInstanceService.previewElevenLabsVoice({
        voiceId: config.ttsVoiceId,
        text: VOICE_PREVIEW_TEXT,
        ...(isSupertonic ? { provider: 'supertonic' } : isFish ? { provider: 'fish' } : isSarvam ? { provider: 'sarvam' } : {}),
      });

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(objectUrl);
      audio.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        if (audioRef.current !== audio) return;
        setPreviewingVoice(false);
        toast.error(
          isSupertonic
            ? 'Voice preview failed. Check that the Supertonic sidecar is running and reachable.'
            : isFish
              ? 'Voice preview failed. Check that a Fish Audio key is configured and the voice (reference) ID is valid.'
              : isSarvam
              ? 'Voice preview failed. Check that a Sarvam key is configured and the speaker name is valid.'
              : 'Voice preview failed. Check that an ElevenLabs key is configured and the voice ID is valid.',
        );
      };
      await audio.play();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to play voice preview.');
    } finally {
      setPreviewingVoice(false);
    }
  };

  // Custom ambient MP3 upload (hold/ambient music section)
  const [uploadingAmbient, setUploadingAmbient] = useState(false);
  const ambientFileRef = useRef<HTMLInputElement>(null);

  const handleAmbientUpload = async (file?: File | null) => {
    if (!file) return;
    if (!/audio\/(mpeg|mp3)/i.test(file.type) && !/\.mp3$/i.test(file.name)) {
      toast.error('Please upload an MP3 file.');
      return;
    }
    setUploadingAmbient(true);
    try {
      const res: any = await fileService.upload(file, { subdir: 'ambient-music' });
      const f = res?.items?.[0] ?? res?.files?.[0] ?? res;
      const key = f?.key;
      const bucket = f?.bucket ?? res?.bucket;
      if (!key || !bucket) throw new Error('Upload did not return a key/bucket');
      setConfig((prev) => ({ ...prev, ambientMusicKey: key, ambientMusicBucket: bucket }));
      toast.success('Ambient music uploaded. Save to apply.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to upload ambient music.');
    } finally {
      setUploadingAmbient(false);
      if (ambientFileRef.current) ambientFileRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);

    try {
      const payload: Record<string, string | number | boolean> = {
        telephonyProvider: config.telephonyProvider,
        twilioAccountSid: config.twilioAccountSid,
        twilioAuthToken: config.twilioAuthToken,
        twilioPhoneNumber: config.twilioPhoneNumber,
        telnyxApiKey: config.telnyxApiKey,
        telnyxPhoneNumber: config.telnyxPhoneNumber,
        ttsProvider: config.ttsProvider,
        ttsApiKey: config.ttsApiKey,
        ttsBaseUrl: config.ttsBaseUrl,
        ttsVoiceId: config.ttsVoiceId,
        ttsModelId: config.ttsModelId,
        sttProvider: config.sttProvider,
        sttApiKey: config.sttApiKey,
        sttModel: config.sttModel,
        language: config.language,
        keyterms: config.keyterms,
        idleTimeoutSeconds: config.idleTimeoutSeconds,
        idleMaxPrompts: config.idleMaxPrompts,
        enableHoldMusic: config.enableHoldMusic,
        enableAmbientMusic: config.enableAmbientMusic,
        ambientMusicKey: config.ambientMusicKey,
        ambientMusicBucket: config.ambientMusicBucket,
      };

      // Declared-field provider in use: send its plain fields, and secrets only when typed in.
      const generic = telephonyProviders.find((p) => p.code === config.telephonyProvider && p.credentialStorage === 'generic');
      const fullPayload: Record<string, unknown> = { ...payload };
      if (generic) {
        const edits = providerFields[generic.code] ?? {};
        const out: Record<string, string> = {};
        for (const f of generic.fields) {
          const v = (edits[f.key] ?? '').trim();
          if (f.secret && !v) continue; // blank secret = keep the saved one
          out[f.key] = v;
        }
        fullPayload.telephonyCredentials = { [generic.code]: out };
      }

      await voiceConfigService.update(tenantId, fullPayload);
      await load();
      setStatus({ tone: 'ok', text: 'Voice configuration saved.' });
    } catch (err: any) {
      setStatus({ tone: 'error', text: err?.details?.message || 'Could not save voice configuration.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-agentic-text-secondary">Loading voice configuration…</div>;

  const tts = TTS_PROVIDER_SPEC[config.ttsProvider] ?? TTS_PROVIDER_SPEC.elevenlabs;

  const field = (key: keyof TenantVoiceConfig, label: string, placeholder: string, hint?: string) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <TextField
        type="text"
        value={String(config[key] ?? '')}
        onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
        placeholder={placeholder}
        className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
      />
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );

  const secret = (key: keyof TenantVoiceConfig, label: string) => {
    const visible = !!visibleSecrets[key];
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="relative">
          <TextField
            type={visible ? 'text' : 'password'}
            value={String(config[key] ?? '')}
            onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
            placeholder="Not set"
            autoComplete="new-password"
            className="w-full rounded-lg border px-3 py-2 pr-16 font-mono text-sm focus:ring-2 focus:ring-agentic-primary-blue"
          />
          <Button tone="custom"
            type="button"
            onClick={() => setVisibleSecrets({ ...visibleSecrets, [key]: !visible })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
          >
            {visible ? 'Hide' : 'Show'}
          </Button>
        </div>
      </div>
    );
  };

  const selectedProvider = telephonyProviders.find((p) => p.code === config.telephonyProvider);

  /** Fields a declared-field provider (Plivo, …) asks for, rendered from the provider itself. */
  const genericProviderFields = () => {
    if (!selectedProvider || selectedProvider.credentialStorage !== 'generic') return null;
    const code = selectedProvider.code;
    const edits = providerFields[code] ?? {};
    const secretsSet = config.telephonyCredentials?.[code]?.secretsSet ?? {};
    const setValue = (key: string, value: string) =>
      setProviderFields((prev) => ({ ...prev, [code]: { ...(prev[code] ?? {}), [key]: value } }));
    return selectedProvider.fields.map((f) => {
      const visKey = `${code}.${f.key}`;
      const visible = !!visibleSecrets[visKey];
      return (
        <div key={visKey} className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">
            {f.label}
            {f.required ? '' : ' (optional)'}
          </label>
          <div className={f.secret ? 'relative' : undefined}>
            <TextField
              type={f.secret && !visible ? 'password' : 'text'}
              value={edits[f.key] ?? ''}
              onChange={(e) => setValue(f.key, e.target.value)}
              placeholder={f.secret ? (secretsSet[f.key] ? 'Saved — leave blank to keep' : 'Not set') : f.placeholder ?? ''}
              autoComplete={f.secret ? 'new-password' : undefined}
              className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue${f.secret ? ' pr-16 font-mono text-sm' : ''}`}
            />
            {f.secret && (
              <Button tone="custom"
                type="button"
                onClick={() => setVisibleSecrets({ ...visibleSecrets, [visKey]: !visible })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
              >
                {visible ? 'Hide' : 'Show'}
              </Button>
            )}
          </div>
          {f.help && <p className="text-xs text-gray-500">{f.help}</p>}
        </div>
      );
    });
  };

  /** Be explicit about what the chosen carrier can't do here, instead of failing mid-call. */
  const capabilityNote = () => {
    const caps = selectedProvider?.capabilities;
    if (!caps) return null;
    const missing = [
      !caps.outbound && 'outbound calling',
      !caps.warmTransfer && 'live transfer to a human',
    ].filter(Boolean);
    return missing.length ? (
      <p className="mt-3 text-xs text-amber-700">
        {selectedProvider!.label} on this platform does not support {missing.join(' or ')} yet.
      </p>
    ) : null;
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold">Voice configuration</h2>
        <p className="mt-1 text-sm text-agentic-text-secondary">
          This tenant is whitelabel, so calls go out on its own carrier account and speech is
          billed to its own keys. Without these, voice is refused rather than falling back to the
          platform's account.
        </p>
      </div>

      <section className="rounded-xl border bg-gray-50 p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Telephony</h3>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Provider</label>
            <SelectField
              value={config.telephonyProvider}
              onChange={(e) =>
                setConfig({ ...config, telephonyProvider: e.target.value as TelephonyProvider })
              }
              className="rounded-lg border px-3 py-2"
            >
              {(telephonyProviders.length
                ? telephonyProviders.map((p) => [p.code, p.label] as const)
                : Object.entries(TELEPHONY_PROVIDER_SPEC).map(([code, spec]) => [code, spec.label] as const)
              ).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
          </div>

          {config.telephonyProvider === 'twilio' ? (
            <>
              {field('twilioAccountSid', 'Account SID', 'AC…')}
              {secret('twilioAuthToken', 'Auth token')}
              {field('twilioPhoneNumber', 'Caller ID / from number', '+15551234567')}
            </>
          ) : config.telephonyProvider === 'telnyx' ? (
            <>
              {secret('telnyxApiKey', 'API key')}
              {field('telnyxPhoneNumber', 'Caller ID / from number', '+15551234567')}
            </>
          ) : (
            genericProviderFields()
          )}
        </div>
        {capabilityNote()}
      </section>

      <Surface tone="plain" as="section" className="rounded-xl border p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Text to speech</h3>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Provider</label>
            <SelectField
              value={config.ttsProvider}
              onChange={(e) => handleProviderChange(e.target.value as TtsProvider)}
              className="rounded-lg border px-3 py-2"
            >
              {Object.entries(TTS_PROVIDER_SPEC).map(([value, spec]) => (
                <option key={value} value={value}>
                  {spec.label}
                </option>
              ))}
            </SelectField>
            {tts.note && <p className="text-xs text-gray-500">{tts.note}</p>}
          </div>

          {tts.needsApiKey && secret('ttsApiKey', 'API key')}
          {tts.needsBaseUrl &&
            field('ttsBaseUrl', 'Service URL', 'https://tts.internal.example', 'Where this provider is reachable.')}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">{tts.voiceLabel}</label>

            {isElevenLabs ? (
              loadingVoices ? (
                <div className="rounded-lg border px-3 py-2 text-sm text-gray-400 bg-gray-50">Loading voices…</div>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <SelectField
                      value={customVoiceMode ? CUSTOM_VOICE_OPTION : config.ttsVoiceId}
                      onChange={(e) => handleElevenVoiceSelect(e.target.value)}
                      className="min-w-0 flex-1 rounded-lg border px-3 py-2"
                    >
                      {elevenVoices.length === 0 && !customVoiceMode && (
                        <option value={config.ttsVoiceId}>{config.ttsVoiceId}</option>
                      )}
                      {elevenVoices.map((v) => (
                        <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                      ))}
                      <option value={CUSTOM_VOICE_OPTION}>➕ Custom Voice ID…</option>
                    </SelectField>
                    <Button tone="outline-white"
                      type="button"
                      onClick={handlePreviewVoice}
                      disabled={loadingVoices || previewingVoice || !config.ttsVoiceId}
                      className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {previewingVoice ? 'Playing...' : 'Play'}
                    </Button>
                  </div>
                  {customVoiceMode && (
                    <TextField
                      type="text"
                      value={config.ttsVoiceId}
                      onChange={(e) => setConfig({ ...config, ttsVoiceId: e.target.value.trim() })}
                      placeholder={tts.voicePlaceholder}
                      className="w-full rounded-lg border px-3 py-2 font-mono text-sm"
                    />
                  )}
                  {voicesWarning && <p className="text-xs text-amber-600">{voicesWarning}</p>}
                </div>
              )
            ) : isSupertonic ? (
              <div className="flex gap-2">
                <SelectField
                  value={config.ttsVoiceId}
                  onChange={(e) => setConfig({ ...config, ttsVoiceId: e.target.value })}
                  className="min-w-0 flex-1 rounded-lg border px-3 py-2"
                >
                  {SUPERTONIC_VOICE_OPTIONS.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </SelectField>
                <Button tone="outline-white"
                  type="button"
                  onClick={handlePreviewVoice}
                  disabled={previewingVoice || !config.ttsVoiceId}
                  className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {previewingVoice ? 'Playing...' : 'Play'}
                </Button>
              </div>
            ) : isSarvam ? (
              <div className="space-y-1">
                <div className="flex gap-2">
                  <SelectField
                    value={customVoiceMode || !SARVAM_VOICE_OPTIONS.some((v) => v.value === config.ttsVoiceId) ? CUSTOM_VOICE_OPTION : config.ttsVoiceId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomVoiceMode(value === CUSTOM_VOICE_OPTION);
                      if (value !== CUSTOM_VOICE_OPTION) setConfig({ ...config, ttsVoiceId: value });
                    }}
                    className="min-w-0 flex-1 rounded-lg border px-3 py-2"
                  >
                    {SARVAM_VOICE_OPTIONS.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                    <option value={CUSTOM_VOICE_OPTION}>➕ Other speaker…</option>
                  </SelectField>
                  <Button tone="outline-white"
                    type="button"
                    onClick={handlePreviewVoice}
                    disabled={previewingVoice || !config.ttsVoiceId}
                    className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {previewingVoice ? 'Playing...' : 'Play'}
                  </Button>
                </div>
                {(customVoiceMode || !SARVAM_VOICE_OPTIONS.some((v) => v.value === config.ttsVoiceId)) && (
                  <TextField
                    type="text"
                    value={config.ttsVoiceId}
                    onChange={(e) => setConfig({ ...config, ttsVoiceId: e.target.value.trim() })}
                    placeholder={tts.voicePlaceholder}
                    className="w-full rounded-lg border px-3 py-2 font-mono text-sm"
                  />
                )}
              </div>
            ) : isFish ? (
              loadingVoices ? (
                <div className="rounded-lg border px-3 py-2 text-sm text-gray-400 bg-gray-50">Loading voices…</div>
              ) : (
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <SelectField
                      value={customVoiceMode ? CUSTOM_VOICE_OPTION : (config.ttsVoiceId || FISH_DEFAULT_OPTION)}
                      onChange={(e) => handleFishVoiceSelect(e.target.value)}
                      className="min-w-0 flex-1 rounded-lg border px-3 py-2"
                    >
                      <option value={FISH_DEFAULT_OPTION}>Default voice</option>
                      {fishVoices.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}{v.languages?.length ? ` (${v.languages.join(', ')})` : ''}
                        </option>
                      ))}
                      <option value={CUSTOM_VOICE_OPTION}>➕ Custom reference ID…</option>
                    </SelectField>
                    <Button tone="outline-white"
                      type="button"
                      onClick={handlePreviewVoice}
                      disabled={previewingVoice}
                      className="shrink-0 inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {previewingVoice ? 'Playing...' : 'Play'}
                    </Button>
                  </div>
                  {customVoiceMode && (
                    <TextField
                      type="text"
                      value={config.ttsVoiceId}
                      onChange={(e) => setConfig({ ...config, ttsVoiceId: e.target.value.trim() })}
                      placeholder={tts.voicePlaceholder}
                      className="w-full rounded-lg border px-3 py-2 font-mono text-sm"
                    />
                  )}
                  {voicesWarning && <p className="text-xs text-amber-600">{voicesWarning}</p>}
                </div>
              )
            ) : (
              <SelectField
                value={config.ttsVoiceId}
                onChange={(e) => setConfig({ ...config, ttsVoiceId: e.target.value })}
                className="w-full rounded-lg border px-3 py-2"
              >
                {TWILIO_VOICE_OPTIONS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </SelectField>
            )}
          </div>

          {tts.modelPlaceholder && field('ttsModelId', 'Model', tts.modelPlaceholder)}
        </div>
        <p className="text-xs text-gray-400">
          Voice listing/preview currently use the platform's own key for this provider, not this tenant's — useful
          to browse available voices, but the voice ID you save here is what actually plays on this tenant's calls.
        </p>
      </Surface>

      <Surface tone="plain" as="section" className="rounded-xl border p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Speech to text</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Provider</label>
            <SelectField
              value={config.sttProvider}
              onChange={(e) => setConfig({ ...config, sttProvider: e.target.value as any })}
              className="rounded-lg border px-3 py-2"
            >
              <option value="deepgram">Deepgram</option>
              <option value="sarvam">Sarvam AI (Indian languages, Hindi + English)</option>
            </SelectField>
            {config.sttProvider === 'sarvam' && (
              <p className="text-xs text-gray-500">
                Supports English and Hindi (incl. Hindi + English mixed). Calls in other languages use Deepgram.
              </p>
            )}
          </div>
          {secret('sttApiKey', 'API key')}
          {field('sttModel', 'Model', config.sttProvider === 'sarvam' ? 'saaras:v4' : 'nova-3')}
        </div>
      </Surface>

      <Surface tone="plain" as="section" className="rounded-xl border p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Language &amp; recognition</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Language</label>
            <SelectField
              value={config.language}
              onChange={(e) => setConfig({ ...config, language: e.target.value })}
              className="rounded-lg border px-3 py-2"
            >
              {languageOptions.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
              {config.language && !languageOptions.some((l) => l.code === config.language) && (
                // A value saved before the current list (e.g. 'de-DE') stays visible and selected.
                <option value={config.language}>{config.language}</option>
              )}
            </SelectField>
            <p className="text-xs text-gray-500">
              Callers are understood and answered in this language. Choose a voice that speaks it.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              Speech keyterms <span className="text-gray-400 font-normal">(improves recognition)</span>
            </label>
            <TextArea
              rows={2}
              value={config.keyterms}
              onChange={(e) => setConfig({ ...config, keyterms: e.target.value })}
              placeholder="e.g. Bellus Academy, Poway, Temecula, cosmetology, esthetics"
              className="rounded-lg border px-3 py-2"
            />
            <p className="text-xs text-gray-500">
              Comma-separated domain terms (names, specialties) boosted in speech recognition — Deepgram Nova-3, or
              Sarvam saaras:v4 (first 50 terms). Leave blank to disable.
            </p>
          </div>
        </div>
      </Surface>

      <Surface tone="plain" as="section" className="rounded-xl border p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">No-response prompts</h3>
        <p className="mb-4 text-xs text-gray-500">
          Timing only — the spoken wording of idle/goodbye prompts stays configured per agent, in that agent's own
          Voice Config tab.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Silence before prompting (seconds)</label>
            <TextField
              type="number"
              min={3}
              max={60}
              value={config.idleTimeoutSeconds}
              onChange={(e) => setConfig({ ...config, idleTimeoutSeconds: Number(e.target.value) })}
              className="rounded-lg border px-3 py-2"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Max prompts before hanging up</label>
            <TextField
              type="number"
              min={1}
              max={5}
              value={config.idleMaxPrompts}
              onChange={(e) => setConfig({ ...config, idleMaxPrompts: Number(e.target.value) })}
              className="rounded-lg border px-3 py-2"
            />
          </div>
        </div>
      </Surface>

      <Surface tone="plain" as="section" className="rounded-xl border p-6 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Hold &amp; ambient music</h3>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={config.enableHoldMusic}
            onChange={(e) => setConfig({ ...config, enableHoldMusic: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm">
            <span className="block font-medium text-gray-700">Enable hold music</span>
            <span className="block text-gray-400 font-normal">
              Play hold music while the assistant is thinking. When off, the softer ambient sound keeps playing instead.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={config.enableAmbientMusic}
            onChange={(e) => setConfig({ ...config, enableAmbientMusic: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm">
            <span className="block font-medium text-gray-700">Enable ambient music</span>
            <span className="block text-gray-400 font-normal">
              Play an ambient sound while the assistant is thinking, before it starts speaking. You can upload a
              custom MP3 below.
            </span>
          </span>
        </label>

        {config.enableAmbientMusic && (
          <div className="ml-7 space-y-2">
            <label className="block text-xs font-medium text-gray-600">Custom ambient music (MP3)</label>
            <div className="flex flex-wrap items-center gap-2">
              <Button tone="custom"
                type="button"
                onClick={() => ambientFileRef.current?.click()}
                disabled={uploadingAmbient}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {uploadingAmbient ? 'Uploading…' : 'Upload MP3'}
              </Button>
              {config.ambientMusicKey ? (
                <>
                  <span className="truncate text-xs text-gray-500">{config.ambientMusicKey.split('/').pop()}</span>
                  <Button tone="custom"
                    type="button"
                    onClick={() => setConfig({ ...config, ambientMusicKey: '', ambientMusicBucket: '' })}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <span className="text-xs text-gray-400">Using the built-in ambient sound.</span>
              )}
            </div>
            <input
              ref={ambientFileRef}
              type="file"
              accept="audio/mpeg,.mp3"
              className="hidden"
              onChange={(e) => handleAmbientUpload(e.target.files?.[0])}
            />
          </div>
        )}
      </Surface>

      {status && (
        <p
          className={`rounded-lg px-4 py-3 text-sm ${
            status.tone === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
          }`}
        >
          {status.text}
        </p>
      )}

      <div className="flex justify-end">
        <Button tone="primary"
          onClick={save}
          disabled={saving}
          className="rounded-lg px-6 py-2 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save configuration'}
        </Button>
      </div>
    </div>
  );
};

export default VoiceCredentials;
